import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useT } from '../i18n';

type StepKey = 'connection' | 'contacts' | 'campaign' | 'send' | 'inbox';

interface Step {
  key: StepKey;
  done: boolean;
  count: number;
}

interface OnboardingState {
  applicable: boolean;
  completed: boolean;
  steps: Step[];
  nextStep: StepKey | null;
  doneCount: number;
  total: number;
}

// Cada passo aponta para a tela onde ele é feito
const STEP_PATH: Record<StepKey, string> = {
  connection: '/whatsapp',
  contacts: '/contacts',
  campaign: '/campaigns',
  send: '/campaigns',
  inbox: '/inbox',
};

const HIDDEN_KEY = 'onboarding_hidden';
const WELCOMED_KEY = 'onboarding_welcomed';

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string) {
  try {
    localStorage.setItem(key, '1');
  } catch {
    // Navegador sem armazenamento: o guia volta na próxima visita, o que não quebra nada
  }
}

interface ContextValue {
  state: OnboardingState | null;
  hidden: boolean;
  hide: () => void;
  reload: () => void;
}

const OnboardingContext = createContext<ContextValue>({ state: null, hidden: false, hide: () => {}, reload: () => {} });

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OnboardingState | null>(null);
  const [hidden, setHidden] = useState(readFlag(HIDDEN_KEY));
  const location = useLocation();

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const response = await fetch('/api/onboarding', { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) return;
      setState(await response.json());
    } catch {
      // Sem o estado, o guia simplesmente não aparece
    }
  }, []);

  // Recarrega ao trocar de tela: o passo pode ter sido concluído na anterior
  useEffect(() => {
    load();
  }, [load, location.pathname]);

  const value = useMemo<ContextValue>(() => ({
    state,
    hidden,
    hide: () => { setHidden(true); writeFlag(HIDDEN_KEY); },
    reload: load,
  }), [state, hidden, load]);

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export const useOnboarding = () => useContext(OnboardingContext);

function StepRow({ step, index, isNext, onNavigate }: {
  step: Step; index: number; isNext: boolean; onNavigate: () => void;
}) {
  const t = useT();
  const location = useLocation();
  // Já estando na tela do passo, o botão só repetiria onde a pessoa está
  const alreadyHere = location.pathname === STEP_PATH[step.key];

  return (
    <div className={`flex items-start gap-3 rounded-xl p-3 ${isNext ? 'bg-emerald-50 border border-emerald-200' : ''}`}>
      <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
        step.done ? 'bg-emerald-600 text-white' : isNext ? 'bg-white border-2 border-emerald-500 text-emerald-700' : 'bg-gray-100 text-gray-500'
      }`}>
        {step.done ? '✓' : index + 1}
      </span>

      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${step.done ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
          {t(`ob.${step.key}.title`)}
        </p>
        {!step.done && isNext && (
          <p className="text-xs text-gray-600 mt-1 leading-relaxed">{t(`ob.${step.key}.text`)}</p>
        )}
        {step.done && (
          <p className="text-xs text-emerald-700 mt-0.5">
            {t(`ob.${step.key}.doneText`, { count: step.count.toLocaleString('en-US') })}
          </p>
        )}
      </div>

      {isNext && !alreadyHere && (
        <Link
          to={STEP_PATH[step.key]}
          onClick={onNavigate}
          className="shrink-0 text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 whitespace-nowrap"
        >
          {t(`ob.${step.key}.action`)}
        </Link>
      )}
    </div>
  );
}

/** Cartão com os primeiros passos. Some sozinho quando tudo está feito. */
export function OnboardingGuide({ compact = false }: { compact?: boolean }) {
  const t = useT();
  const { state, hidden, hide, reload } = useOnboarding();
  const [justFinished, setJustFinished] = useState(false);

  useEffect(() => {
    if (state?.completed && state.applicable && !readFlag(WELCOMED_KEY + '_done')) {
      setJustFinished(true);
      writeFlag(WELCOMED_KEY + '_done');
    }
  }, [state?.completed, state?.applicable]);

  if (!state?.applicable || hidden) return null;

  if (state.completed) {
    if (!justFinished) return null;
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-emerald-900">{t('ob.banner.finished')}</p>
          <p className="text-sm text-emerald-800 mt-1">{t('ob.banner.finishedText')}</p>
        </div>
        <button onClick={() => setJustFinished(false)} className="text-emerald-700 hover:text-emerald-900 p-1" title={t('ob.hide')}>✕</button>
      </div>
    );
  }

  const percentage = Math.round((state.doneCount / state.total) * 100);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-gray-900">{t('ob.title')}</h3>
          {!compact && <p className="text-xs text-gray-500 mt-0.5">{t('ob.subtitle')}</p>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {t('ob.progress', { done: state.doneCount, total: state.total })}
          </span>
          <button onClick={hide} className="text-gray-400 hover:text-gray-600 text-xs" title={t('ob.hide')}>✕</button>
        </div>
      </div>

      <div className="h-1.5 bg-gray-100 rounded-full mt-3 overflow-hidden" dir="ltr">
        <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${percentage}%` }} />
      </div>

      <div className="mt-3 space-y-1">
        {state.steps
          // Em modo compacto mostramos só o passo atual, para não roubar a tela
          .filter(step => !compact || step.key === state.nextStep)
          .map(step => (
            <StepRow
              key={step.key}
              step={step}
              index={state.steps.findIndex(s => s.key === step.key)}
              isNext={step.key === state.nextStep}
              onNavigate={reload}
            />
          ))}
      </div>
    </div>
  );
}

/** Boas-vindas na primeira entrada, uma única vez */
export function OnboardingWelcome() {
  const t = useT();
  const { state } = useOnboarding();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!state?.applicable || state.completed) return;
    // Só para quem ainda não fez nada, e só uma vez
    if (state.doneCount === 0 && !readFlag(WELCOMED_KEY)) {
      setOpen(true);
      writeFlag(WELCOMED_KEY);
    }
  }, [state]);

  if (!open || !state) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
        <h2 className="text-xl font-bold text-gray-900">{t('ob.welcome.title')}</h2>
        <p className="mt-2 text-gray-600 leading-relaxed">{t('ob.welcome.text')}</p>

        <ol className="mt-5 space-y-3">
          {state.steps.map((step, index) => (
            <li key={step.key} className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold flex items-center justify-center">
                {index + 1}
              </span>
              <div>
                <p className="text-sm font-medium text-gray-900">{t(`ob.${step.key}.title`)}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t(`ob.${step.key}.text`)}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
            {t('ob.welcome.later')}
          </button>
          <Link
            to={STEP_PATH[state.nextStep || 'connection']}
            onClick={() => setOpen(false)}
            className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
          >
            {t('ob.welcome.start')}
          </Link>
        </div>
      </div>
    </div>
  );
}
